import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Save, RefreshCw, Search, FileDown, Sparkles, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import EnergiebilanzPanel from "@/components/nutrition/EnergiebilanzPanel";
import { MEAL_CONFIG, UI_LABELS } from "@/components/nutrition/nutritionConstants";
import { NutritionSidebar } from "@/components/nutrition/NutritionSidebar";
import { MacrosDashboard } from "@/components/nutrition/MacrosDashboard";
import { MealTable } from "@/components/nutrition/MealTable";
import { AddFoodDialog } from "@/components/nutrition/AddFoodDialog";
import { EditFoodDialog } from "@/components/nutrition/EditFoodDialog";
import { AIGenerateDialog } from "@/components/nutrition/AIGenerateDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export default function NutritionPlanPage({ onLogout }) {
  const [plans, setPlans] = useState([]);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [planName, setPlanName] = useState("");
  const [meals, setMeals] = useState({});
  const [activeOptions, setActiveOptions] = useState({});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [foodDB, setFoodDB] = useState([]);
  const [foodCategories, setFoodCategories] = useState([]);
  const [bioClients, setBioClients] = useState([]);
  const [crmClients, setCrmClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientSearch, setClientSearch] = useState("");
  const [showAddFood, setShowAddFood] = useState(false);
  const [targetMeal, setTargetMeal] = useState("breakfast");
  const [foodFilter, setFoodFilter] = useState("");
  const [catFilter, setCatFilter] = useState("");
  const [collapsedMeals, setCollapsedMeals] = useState({});
  const [showCreateFood, setShowCreateFood] = useState(false);
  const [editingFood, setEditingFood] = useState(null);
  const [newFood, setNewFood] = useState({ name: "", category: "proteina", calories: "", protein: "", carbs: "", fat: "", description: "", serving_size: "100" });
  const [showAIDialog, setShowAIDialog] = useState(false);
  const [aiObjective, setAiObjective] = useState("Gesunde Ernährung");
  const [recipes, setRecipes] = useState({});
  const [loadingRecipes, setLoadingRecipes] = useState({});
  const [showRecipes, setShowRecipes] = useState({});
  const [energiebilanz, setEnergiebilanz] = useState({});
  const [ebTargets, setEbTargets] = useState(null);
  const [foodLang, setFoodLang] = useState("de");
  const [pdfLang, setPdfLang] = useState("de");
  const autoSaveTimer = useRef(null);
  const [autoSaveStatus, setAutoSaveStatus] = useState(null);
  const isInitialLoad = useRef(true);

  const L = UI_LABELS[foodLang] || UI_LABELS.de;
  const getMealName = (mc) => mc[foodLang] || mc.de;

  const getFoodName = (item) => {
    if (foodLang === "de" && item.name_de) return item.name_de;
    if (foodLang === "en" && item.name_en) return item.name_en;
    if (foodLang === "pt") return item.name;
    const dbFood = foodDB.find(f => f.id === item.food_id || f.name === item.name);
    if (dbFood) {
      if (foodLang === "de" && dbFood.name_de) return dbFood.name_de;
      if (foodLang === "en" && dbFood.name_en) return dbFood.name_en;
    }
    return item.name;
  };

  const getCatName = (cat) => {
    if (foodLang === "de" && cat.name_de) return cat.name_de;
    if (foodLang === "en" && cat.name_en) return cat.name_en;
    return cat.name;
  };

  const getFoodDesc = (item) => {
    if (item.description) return item.description;
    const dbFood = foodDB.find(f => f.id === item.food_id || f.name === item.name);
    return dbFood?.description || "";
  };

  // --- Data Loading ---
  const loadData = useCallback(async () => {
    try {
      const [plansRes, foodsRes, bioRes, crmRes] = await Promise.all([
        axios.get(`${API}/nutrition-plans`, { headers: getHeaders() }),
        axios.get(`${API}/foods`, { headers: getHeaders() }),
        axios.get(`${API}/bio/clients`, { headers: getHeaders() }).catch(() => ({ data: { clients: [] } })),
        axios.get(`${API}/crm/clients`, { headers: getHeaders() }).catch(() => ({ data: { clients: [] } })),
      ]);
      setPlans(plansRes.data.plans || []);
      setFoodDB(foodsRes.data.foods || []);
      setFoodCategories(foodsRes.data.categories || []);
      setBioClients(bioRes.data.clients || []);
      setCrmClients(crmRes.data.clients || []);
    } catch { toast.error("Fehler beim Laden"); }
  }, []);

  useEffect(() => { loadData(); }, []);

  // Restore draft from localStorage on mount
  useEffect(() => {
    const draft = localStorage.getItem("nutrition_plan_draft");
    if (draft) {
      try {
        const d = JSON.parse(draft);
        if (d.planName && d.meals && Object.values(d.meals).some(opts => opts.some(opt => opt.length > 0))) {
          setPlanName(d.planName);
          setMeals(d.meals);
          if (d.energiebilanz) setEnergiebilanz(d.energiebilanz);
          toast.info("Entwurf wiederhergestellt", { duration: 3000 });
        }
      } catch { /* invalid draft */ }
    }
  }, []);

  // Save draft to localStorage for unsaved plans
  useEffect(() => {
    if (isInitialLoad.current) return;
    if (!currentPlan && planName.trim() && Object.values(meals).some(opts => Array.isArray(opts) && opts.some(opt => Array.isArray(opt) && opt.length > 0))) {
      localStorage.setItem("nutrition_plan_draft", JSON.stringify({ planName, meals, energiebilanz }));
    }
  }, [meals, planName, energiebilanz, currentPlan]);

  // Auto-fill Energiebilanz from Bioimpedanz when selecting a client
  useEffect(() => {
    if (!selectedClient) return;
    const fetchBioData = async () => {
      try {
        const res = await axios.get(`${API}/bio/clients/${selectedClient.id}`, { headers: getHeaders() });
        const diagnoses = res.data.diagnoses || [];
        if (diagnoses.length === 0) return;
        const latest = diagnoses[diagnoses.length - 1];
        const updates = {};
        if (latest.gewicht) updates.gewicht = String(latest.gewicht);
        if (Object.keys(updates).length > 0) {
          setEnergiebilanz(prev => {
            if (prev.gewicht) return prev;
            return { ...prev, ...updates };
          });
          toast.success(`Bioimpedanz-Daten geladen: ${latest.gewicht} kg`, { duration: 3000 });
        }
      } catch (err) { console.error("Bio fetch failed:", err); /* Client may not have bio data */ }
    };
    fetchBioData();
    if (selectedClient.geschlecht) {
      const g = selectedClient.geschlecht === "w" ? "frau" : "mann";
      setEnergiebilanz(prev => prev.geschlecht ? prev : { ...prev, geschlecht: g });
    }
  }, [selectedClient]);

  // --- Meal Helpers ---
  const initMeals = () => {
    const m = {};
    MEAL_CONFIG.forEach(mc => { m[mc.id] = [[], [], []]; });
    return m;
  };

  const getItems = (mealId) => {
    const raw = meals[mealId];
    if (!raw) return [];
    if (Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0])) {
      return raw[activeOptions[mealId] || 0] || [];
    }
    return raw;
  };

  const loadPlan = (plan) => {
    isInitialLoad.current = true;
    setCurrentPlan(plan);
    setPlanName(plan.name);
    const rawMeals = plan.meals || initMeals();
    const normalized = {};
    MEAL_CONFIG.forEach(mc => {
      const raw = rawMeals[mc.id];
      if (Array.isArray(raw) && raw.length > 0 && Array.isArray(raw[0])) {
        normalized[mc.id] = [...raw];
        while (normalized[mc.id].length < 3) normalized[mc.id].push([]);
      } else if (Array.isArray(raw)) {
        normalized[mc.id] = [raw, [], []];
      } else {
        normalized[mc.id] = [[], [], []];
      }
    });
    setMeals(normalized);
    setActiveOptions({});
    setEnergiebilanz(plan.energiebilanz || {});
    setEbTargets(null);
    const allCl = [...bioClients, ...crmClients];
    const client = allCl.find(c => c.id === plan.client_id);
    setSelectedClient(client || null);
    setClientSearch(client ? `${client.vorname} ${client.nachname}` : "");
    localStorage.removeItem("nutrition_plan_draft");
  };

  const resetForm = () => {
    isInitialLoad.current = true;
    setCurrentPlan(null); setPlanName(""); setMeals(initMeals());
    setSelectedClient(null); setClientSearch(""); setActiveOptions({});
    setEnergiebilanz({}); setEbTargets(null); setAutoSaveStatus(null);
    localStorage.removeItem("nutrition_plan_draft");
  };

  // --- CRUD Handlers ---
  const handleSave = async () => {
    if (!planName.trim()) { toast.error("Bitte Plan-Name eingeben"); return; }
    setSaving(true);
    try {
      const payload = { name: planName, client_id: selectedClient?.id || null, meals, energiebilanz };
      if (currentPlan) {
        await axios.put(`${API}/nutrition-plans/${currentPlan.id}`, payload, { headers: getHeaders() });
      } else {
        const res = await axios.post(`${API}/nutrition-plans`, payload, { headers: getHeaders() });
        setCurrentPlan(res.data);
        toast.success("Plan erstellt");
      }
      await loadData();
      setAutoSaveStatus("saved");
      localStorage.removeItem("nutrition_plan_draft");
    } catch { toast.error("Fehler beim Speichern"); }
    setSaving(false);
  };

  // Auto-save
  useEffect(() => {
    if (isInitialLoad.current || !currentPlan || !planName.trim()) return;
    setAutoSaveStatus(null);
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      setAutoSaveStatus("saving");
      try {
        const payload = { name: planName, client_id: selectedClient?.id || null, meals, energiebilanz };
        await axios.put(`${API}/nutrition-plans/${currentPlan.id}`, payload, { headers: getHeaders() });
        setAutoSaveStatus("saved");
        const plansRes = await axios.get(`${API}/nutrition-plans`, { headers: getHeaders() });
        setPlans(plansRes.data.plans || []);
      } catch (err) { console.error("Auto-save failed:", err); setAutoSaveStatus(null); }
    }, 1500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [meals, planName, energiebilanz, selectedClient]);

  useEffect(() => {
    if (isInitialLoad.current) {
      const timer = setTimeout(() => { isInitialLoad.current = false; }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentPlan]);

  const handleDelete = async (planId) => {
    try {
      await axios.delete(`${API}/nutrition-plans/${planId}`, { headers: getHeaders() });
      toast.success("Plan gelöscht"); await loadData();
      if (currentPlan?.id === planId) resetForm();
    } catch { toast.error("Fehler beim Löschen"); }
  };

  const handleExportPDF = async (planId) => {
    try {
      if (currentPlan?.id === planId) {
        await axios.put(`${API}/nutrition-plans/${planId}`, { name: planName, client_id: selectedClient?.id || null, meals, energiebilanz }, { headers: getHeaders() });
      }
      const res = await axios.get(`${API}/nutrition-plans/${planId}/pdf?lang=${pdfLang}`, { headers: getHeaders(), responseType: "blob", timeout: 60000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `Ernaehrungsplan_${planName || "Plan"}_${pdfLang.toUpperCase()}.pdf`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen");
    } catch { toast.error("PDF-Export fehlgeschlagen"); }
  };

  const handleShoppingList = async (planId) => {
    try {
      if (currentPlan?.id === planId) {
        await axios.put(`${API}/nutrition-plans/${planId}`, { name: planName, client_id: selectedClient?.id || null, meals, energiebilanz }, { headers: getHeaders() });
      }
      const res = await axios.get(`${API}/nutrition-plans/${planId}/shopping-list?lang=${pdfLang}`, { headers: getHeaders(), responseType: "blob", timeout: 30000 });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a"); link.href = url;
      link.setAttribute("download", `Einkaufsliste_${planName || "Plan"}_${pdfLang.toUpperCase()}.pdf`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Einkaufsliste heruntergeladen");
    } catch { toast.error("Einkaufsliste fehlgeschlagen"); }
  };

  // --- Food Handlers ---
  const addFoodToMeal = (food) => {
    const optIdx = activeOptions[targetMeal] || 0;
    const item = {
      food_id: food.id, name: food.name, name_de: food.name_de || "", name_en: food.name_en || "",
      description: food.description || "", image: food.image || "",
      weight: food.serving_size || 100, serving_size: food.serving_size || 100,
      calories_per100: food.calories, protein_per100: food.protein,
      carbs_per100: food.carbs, fat_per100: food.fat,
    };
    setMeals(prev => {
      const mealOpts = [...(prev[targetMeal] || [[], [], []])];
      mealOpts[optIdx] = [...(mealOpts[optIdx] || []), item];
      return { ...prev, [targetMeal]: mealOpts };
    });
    setShowAddFood(false);
    toast.success(`${getFoodName(food)} → ${getMealName(MEAL_CONFIG.find(m => m.id === targetMeal))} (${L.opt[optIdx]})`);
  };

  const updateFoodWeight = (mealId, idx, weight) => {
    const optIdx = activeOptions[mealId] || 0;
    setMeals(prev => {
      const mealOpts = [...(prev[mealId] || [[], [], []])];
      mealOpts[optIdx] = mealOpts[optIdx].map((item, i) => i === idx ? { ...item, weight: parseFloat(weight) || 0 } : item);
      return { ...prev, [mealId]: mealOpts };
    });
  };

  const removeFoodFromMeal = (mealId, idx) => {
    const optIdx = activeOptions[mealId] || 0;
    setMeals(prev => {
      const mealOpts = [...(prev[mealId] || [[], [], []])];
      mealOpts[optIdx] = mealOpts[optIdx].filter((_, i) => i !== idx);
      return { ...prev, [mealId]: mealOpts };
    });
  };

  const handleSuggestRecipes = async (mealId) => {
    const items = getItems(mealId);
    if (items.length === 0) { toast.error("Fügen Sie zuerst Lebensmittel hinzu"); return; }
    setLoadingRecipes(prev => ({ ...prev, [mealId]: true }));
    setShowRecipes(prev => ({ ...prev, [mealId]: true }));
    try {
      const res = await axios.post(`${API}/nutrition-plans/suggest-recipes`, {
        foods: items.map(i => ({ name: i.name, weight: i.weight })), meal_type: mealId,
      }, { headers: getHeaders(), timeout: 60000 });
      setRecipes(prev => ({ ...prev, [mealId]: res.data.recipes }));
    } catch { toast.error("Rezeptvorschläge fehlgeschlagen"); }
    setLoadingRecipes(prev => ({ ...prev, [mealId]: false }));
  };

  const handleAIGenerate = async () => {
    if (!selectedClient) { toast.error("Bitte einen Kunden auswählen"); return; }
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/nutrition-plans/ai-generate`, {
        client_id: selectedClient.id, objective: aiObjective,
      }, { headers: getHeaders(), timeout: 120000 });
      setMeals(res.data.meals);
      if (!planName.trim()) setPlanName(`Ernährungsplan – ${res.data.client_name || selectedClient.vorname}`);
      setShowAIDialog(false);
      toast.success("Ernährungsplan generiert!");
    } catch (err) { toast.error(err?.response?.data?.detail || "KI-Generierung fehlgeschlagen"); }
    setGenerating(false);
  };

  const handleCreateFood = async () => {
    if (!newFood.name.trim()) { toast.error("Name erforderlich"); return; }
    try {
      await axios.post(`${API}/foods`, newFood, { headers: getHeaders() });
      toast.success("Lebensmittel erstellt");
      setShowCreateFood(false);
      setNewFood({ name: "", category: "proteina", calories: "", protein: "", carbs: "", fat: "", description: "", serving_size: "100" });
      loadData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };

  const handleUpdateFood = async (food) => {
    try {
      await axios.put(`${API}/foods/${food.id}`, food, { headers: getHeaders() });
      toast.success("Aktualisiert"); setEditingFood(null); loadData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };

  const handleDeleteFood = async (e, food) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/foods/${food.id}`, { headers: getHeaders() });
      toast.success("Gelöscht"); loadData();
    } catch (err) { toast.error(err?.response?.data?.detail || "Fehler"); }
  };

  // --- Computed Values ---
  const mealMacros = useMemo(() => {
    const result = {};
    MEAL_CONFIG.forEach(mc => {
      const items = getItems(mc.id);
      const totals = { calories: 0, protein: 0, carbs: 0, fat: 0 };
      items.forEach(item => {
        const ss = parseFloat(item.serving_size) || 100;
        const f = (parseFloat(item.weight) || 0) / ss;
        totals.calories += (item.calories_per100 || 0) * f;
        totals.protein += (item.protein_per100 || 0) * f;
        totals.carbs += (item.carbs_per100 || 0) * f;
        totals.fat += (item.fat_per100 || 0) * f;
      });
      result[mc.id] = totals;
    });
    return result;
  }, [meals, activeOptions]);

  const dayTotals = useMemo(() => {
    const t = { calories: 0, protein: 0, carbs: 0, fat: 0 };
    Object.values(mealMacros).forEach(m => { t.calories += m.calories; t.protein += m.protein; t.carbs += m.carbs; t.fat += m.fat; });
    return t;
  }, [mealMacros]);

  const filteredFoods = foodDB.filter(f => {
    const matchText = !foodFilter || f.name.toLowerCase().includes(foodFilter.toLowerCase());
    const matchCat = !catFilter || f.category === catFilter;
    return matchText && matchCat;
  });

  const allClients = useMemo(() => {
    const map = new Map();
    bioClients.forEach(c => map.set(c.id, { ...c, source: "bio" }));
    crmClients.forEach(c => { if (!map.has(c.id)) map.set(c.id, { ...c, source: "crm" }); });
    return [...map.values()];
  }, [bioClients, crmClients]);

  const filteredClients = allClients.filter(c => `${c.vorname} ${c.nachname}`.toLowerCase().includes(clientSearch.toLowerCase()));

  // --- Render ---
  return (
    <Layout onLogout={onLogout}>
      <div className="flex h-full" style={{ height: "calc(100vh - 64px)" }}>
        <NutritionSidebar plans={plans} currentPlan={currentPlan} onLoadPlan={loadPlan} onReset={resetForm} onExportPDF={handleExportPDF} onDelete={handleDelete} />

        <div className="flex-1 overflow-y-auto p-6 bg-slate-50/50">
          {/* Header */}
          <div className="flex items-end gap-4 mb-6">
            <div className="flex-1">
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">{L.planName.replace("...", "")}</label>
              <Input value={planName} onChange={e => setPlanName(e.target.value)} placeholder="Ernährungsplan..." data-testid="nutrition-plan-name" />
            </div>
            <div className="flex-1 relative">
              <label className="text-xs font-semibold text-slate-500 uppercase mb-1 block">Kunde</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-slate-400" />
                <Input value={clientSearch} onChange={e => { setClientSearch(e.target.value); setSelectedClient(null); }} placeholder={L.clientSearch} className="pl-9" />
              </div>
              {clientSearch && !selectedClient && filteredClients.length > 0 && (
                <div className="absolute z-20 top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-40 overflow-auto">
                  {filteredClients.slice(0, 8).map(c => (
                    <button key={c.id} className="w-full text-left px-3 py-2 hover:bg-slate-50 text-sm"
                      onClick={() => { setSelectedClient(c); setClientSearch(`${c.vorname} ${c.nachname}`); }}>
                      {c.vorname} {c.nachname}
                      {c.source === "bio" && <span className="ml-1 text-[9px] text-emerald-500">(Bio)</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <Button onClick={() => setShowAIDialog(true)} className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white gap-1.5" data-testid="ai-generate-btn">
              <Sparkles className="w-4 h-4" /> KI Generieren
            </Button>
          </div>

          <EnergiebilanzPanel data={energiebilanz} onChange={setEnergiebilanz} onCalcUpdate={setEbTargets} />

          <MacrosDashboard mealMacros={mealMacros} dayTotals={dayTotals} ebTargets={ebTargets} getMealName={getMealName} L={L} />

          {MEAL_CONFIG.map(mc => (
            <MealTable key={mc.id} mc={mc} meals={meals} activeOptions={activeOptions} setActiveOptions={setActiveOptions}
              collapsedMeals={collapsedMeals} setCollapsedMeals={setCollapsedMeals} getItems={getItems} mealMacros={mealMacros}
              getMealName={getMealName} getFoodName={getFoodName} getFoodDesc={getFoodDesc}
              updateFoodWeight={updateFoodWeight} removeFoodFromMeal={removeFoodFromMeal}
              setTargetMeal={setTargetMeal} setShowAddFood={setShowAddFood}
              recipes={recipes} showRecipes={showRecipes} setShowRecipes={setShowRecipes}
              loadingRecipes={loadingRecipes} handleSuggestRecipes={handleSuggestRecipes} L={L} />
          ))}

          {/* Footer Controls */}
          <div className="flex items-center justify-between pt-4 mt-2 border-t border-slate-200">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-bold text-slate-400 uppercase">Sprache</span>
                {[{id:"de",l:"DE"},{id:"pt",l:"PT"},{id:"en",l:"EN"}].map(lg => (
                  <button key={lg.id} onClick={() => setFoodLang(lg.id)}
                    className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${foodLang === lg.id ? 'bg-slate-800 text-white' : 'bg-slate-100 text-slate-500'}`}
                    data-testid={`food-lang-${lg.id}`}>{lg.l}</button>
                ))}
              </div>
              {currentPlan && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-bold text-slate-400 uppercase">PDF</span>
                  {[{id:"de",l:"DE"},{id:"pt",l:"PT"}].map(lg => (
                    <button key={lg.id} onClick={() => setPdfLang(lg.id)}
                      className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${pdfLang === lg.id ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-500'}`}
                      data-testid={`pdf-lang-${lg.id}`}>{lg.l}</button>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => handleExportPDF(currentPlan.id)} data-testid="export-nutrition-pdf"><FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF {pdfLang.toUpperCase()}</Button>
                  <Button size="sm" variant="outline" onClick={() => handleShoppingList(currentPlan.id)} data-testid="shopping-list-btn" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50"><ShoppingCart className="w-3.5 h-3.5 mr-1.5" /> {L.shoppingList}</Button>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {autoSaveStatus === "saving" && (
                <span className="flex items-center gap-1 text-[10px] text-slate-400"><RefreshCw className="w-3 h-3 animate-spin" /> {L.saving}</span>
              )}
              {autoSaveStatus === "saved" && (
                <span className="flex items-center gap-1 text-[10px] text-emerald-500" data-testid="auto-save-status"><Save className="w-3 h-3" /> {L.saved}</span>
              )}
              <Button size="sm" onClick={handleSave} disabled={saving} className="bg-slate-900 hover:bg-slate-800 px-6" data-testid="save-nutrition-btn">
                {saving ? <RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
                {currentPlan ? L.update : L.save}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AddFoodDialog open={showAddFood} onOpenChange={setShowAddFood} targetMeal={targetMeal}
        foodFilter={foodFilter} setFoodFilter={setFoodFilter} catFilter={catFilter} setCatFilter={setCatFilter}
        filteredFoods={filteredFoods} foodCategories={foodCategories} addFoodToMeal={addFoodToMeal}
        showCreateFood={showCreateFood} setShowCreateFood={setShowCreateFood}
        newFood={newFood} setNewFood={setNewFood} handleCreateFood={handleCreateFood}
        editingFood={editingFood} setEditingFood={setEditingFood} handleDeleteFood={handleDeleteFood}
        getFoodName={getFoodName} getCatName={getCatName} L={L}
        mealConfigDE={MEAL_CONFIG.find(m => m.id === targetMeal)?.de} />

      <EditFoodDialog editingFood={editingFood} setEditingFood={setEditingFood} handleUpdateFood={handleUpdateFood} L={L} />

      <AIGenerateDialog open={showAIDialog} onOpenChange={setShowAIDialog}
        clientSearch={clientSearch} setClientSearch={setClientSearch}
        selectedClient={selectedClient} setSelectedClient={setSelectedClient}
        filteredClients={filteredClients} aiObjective={aiObjective} setAiObjective={setAiObjective}
        generating={generating} handleAIGenerate={handleAIGenerate} />
    </Layout>
  );
}
